import { useCallback } from "react"
import { useMutation } from "urql"

import { TransferStatus } from "../constants"
import "../graphql/mutations"

import {
  AdvanceTransferDocument,
  type AdvanceTransferFailure,
  type AdvanceTransferMutation,
  type AdvanceTransferMutationVariables,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

const STATUS_TO_SUCCESS_MESSAGE: Partial<Record<TransferStatus, string>> = {
  [TransferStatus.PendingPick]: "Transfer pick confirmed",
}

interface UseAdvanceTransferReturn {
  advanceTransfer: (transferId: string, expectedStatus: TransferStatus) => Promise<boolean>
  isAdvancing: boolean
}

export const useAdvanceTransfer = (): UseAdvanceTransferReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<AdvanceTransferMutation, AdvanceTransferMutationVariables>(
    AdvanceTransferDocument
  )

  const advanceTransfer = useCallback(
    async (transferId: string, expectedStatus: TransferStatus): Promise<boolean> => {
      const { data, error } = await executeMutation({ input: { transferId, expectedStatus } })
      const output = data?.transfer.advanceTransfer
      const hasError = error || output?.__typename === "AdvanceTransferFailure"

      if (hasError) {
        const message = error?.message ?? (output as AdvanceTransferFailure).error.message
        notify({ type: NotificationType.Error, message })
        return false
      }

      notify({
        type: NotificationType.Success,
        message: STATUS_TO_SUCCESS_MESSAGE[expectedStatus] ?? "Transfer updated",
      })
      return true
    },
    [executeMutation, notify]
  )

  return { advanceTransfer, isAdvancing: fetching }
}
